import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader2, ImagePlus } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import {
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  FormGroup,
  InputLabel,
  TextInput,
  SelectInput,
  ErrorText,
  ModalCancelButton,
  ModalSubmitButton,
  HiddenFileInput,
  FilePickerButton,
  FilePreviewRow,
  ImagePreview,
  FileNameText,
  RemoveFileButton,
} from './CreateAndUpdateCouncilAgente.styles';
import {
  createCouncilAgent,
  updateCouncilAgent,
  replaceCouncilLogo,
} from '../../../../services/general';

const CHATBOT_TYPE_OPTIONS = [
  { value: 'SOFTWARE', label: 'Software' },
  { value: 'COUNCIL', label: 'Council' },
];

const MAX_LOGO_SIZE = 5 * 1024 * 1024; // 5MB

const emptyForm = {
  councilName: '',
  agentId: '',
  aliasId: '',
  siteLink: '',
  chatbotType: '',
  siteLogo: null, // File object (new upload) or existing URL string (update mode)
};

const CreateAndUpdateCouncilAgent = ({
  isOpen,
  onClose,
  isUpdate = false,
  initialData,
  onSuccess,
}) => {
  const [form, setForm] = useState(emptyForm);
  const [logoPreview, setLogoPreview] = useState(null);
  const [errors, setErrors] = useState({});
  const fileInputRef = useRef(null);

  // Populate form when opening in update mode.
  useEffect(() => {
    if (!isOpen) return;

    if (isUpdate && initialData) {
      setForm({
        councilName: initialData.councilName || '',
        agentId: initialData.agentId || '',
        aliasId: initialData.aliasId || '',
        siteLink: initialData.siteLink || '',
        chatbotType: initialData.chatbotType || '',
        siteLogo: initialData.siteLogo || null, // existing logo URL string
      });
      setLogoPreview(typeof initialData.siteLogo === 'string' ? initialData.siteLogo : null);
    } else {
      setForm(emptyForm);
      setLogoPreview(null);
    }
    setErrors({});
  }, [isOpen, isUpdate, initialData]);

  const mutation = useMutation({
    mutationFn: (payload) =>
      isUpdate
        ? // Update now sends the full payload (councilName, agentId, aliasId,
          // siteLink, chatbotType, and siteLogo when changed) as FormData —
          // every field is editable in this mode now.
          updateCouncilAgent({ chatbotId: initialData?.id, payload })
        : createCouncilAgent(payload),
    onSuccess: (data) => {
      onSuccess?.(data);
      handleClose();
    },
    onError: (err) => {
      alert(err?.message || `Failed to ${isUpdate ? 'update' : 'create'} council agent.`);
      setErrors((prev) => ({
        ...prev,
        form:
          err?.message ||
          `Failed to ${isUpdate ? 'update' : 'create'} council agent. Please try again.`,
      }));
    },
  });

  // Separate mutation for replacing the logo in update mode only.
  const logoMutation = useMutation({
    mutationFn: (payload) => replaceCouncilLogo(payload),
    onSuccess: () => {
      alert('Logo updated successfully.');
    },
    onError: (err) => {
      alert(err?.message || 'Failed to replace logo. Please try again.');
    },
  });

  const handleClose = () => {
    setForm(emptyForm);
    setLogoPreview(null);
    setErrors({});
    if (fileInputRef.current) fileInputRef.current.value = '';
    onClose();
  };

  const handleFieldChange = (field) => (e) => {
    const { value } = e.target;
    setForm((prev) => ({ ...prev, [field]: value }));
    if (value.trim()) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handlePickLogo = () => fileInputRef.current?.click();

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setErrors((prev) => ({ ...prev, siteLogo: 'Please select an image file.' }));
      e.target.value = '';
      return;
    }

    if (file.size > MAX_LOGO_SIZE) {
      setErrors((prev) => ({ ...prev, siteLogo: 'Image must be under 5MB.' }));
      e.target.value = '';
      return;
    }

    const previousLogo = form.siteLogo;
    const previousPreview = logoPreview;

    setForm((prev) => ({ ...prev, siteLogo: file }));
    setLogoPreview(URL.createObjectURL(file));
    setErrors((prev) => ({ ...prev, siteLogo: undefined }));
    e.target.value = '';

    // In update mode, logo replacement is a separate API call — fire it right away.
    if (isUpdate && initialData?.id) {
      const logoPayload = new FormData();
      logoPayload.append('chatbotId', initialData.id);
      logoPayload.append('logoField', 'logo');
      logoPayload.append('file', file);

      logoMutation.mutate(logoPayload, {
        onError: () => {
          setForm((prev) => ({ ...prev, siteLogo: previousLogo }));
          setLogoPreview(previousPreview);
        },
      });
    }
  };

  const handleRemoveLogo = () => {
    setForm((prev) => ({ ...prev, siteLogo: null }));
    setLogoPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const validate = () => {
    const nextErrors = {};

    if (!form.councilName.trim()) nextErrors.councilName = 'Council name is required';
    if (!form.agentId.trim()) nextErrors.agentId = 'Agent ID is required';
    if (!form.aliasId.trim()) nextErrors.aliasId = 'Alias ID is required';
    if (!form.siteLink.trim()) nextErrors.siteLink = 'Site link is required';
    if (!form.chatbotType) nextErrors.chatbotType = 'Chatbot type is required';
    if (!form.siteLogo) nextErrors.siteLogo = 'Site logo is required';
    return nextErrors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const nextErrors = validate();
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    if (isUpdate) {
      const payload = {
        councilName: form.councilName.trim(),
        agentId: form.agentId.trim(),
        aliasId: form.aliasId.trim(),
        siteLink: form.siteLink.trim(),
        chatbotType: form.chatbotType,
      };

      mutation.mutate(payload);
      return;
    }

    const payload = new FormData();
    payload.append('councilName', form.councilName.trim());
    payload.append('agentId', form.agentId.trim());
    payload.append('aliasId', form.aliasId.trim());
    payload.append('siteLink', form.siteLink.trim());
    payload.append('chatbotType', form.chatbotType);

    if (form.siteLogo instanceof File) {
      payload.append('siteLogo', form.siteLogo);
    }

    mutation.mutate(payload);
  };

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  if (!isOpen) return null;

  const logoFileName = form.siteLogo instanceof File ? form.siteLogo.name : null;

  return createPortal(
    <ModalOverlay
      onClick={handleClose}
      tabIndex={-1}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle id="modal-title">
            {isUpdate ? 'Update Council Agent' : 'Create Council Agent'}
          </ModalTitle>
          <ModalCloseButton onClick={handleClose} type="button" aria-label="Close">
            <X size={16} />
          </ModalCloseButton>
        </ModalHeader>

        <form onSubmit={handleSubmit}>
          <ModalBody>
            <FormGroup>
              <InputLabel htmlFor="council-name">Council name</InputLabel>
              <TextInput
                id="council-name"
                value={form.councilName}
                onChange={handleFieldChange('councilName')}
                placeholder="Enter council name"
                $hasError={!!errors.councilName}
                disabled={mutation.isPending}
                aria-describedby={errors.councilName ? 'council-name-error' : undefined}
              />
              {errors.councilName && (
                <ErrorText id="council-name-error" role="alert">
                  {errors.councilName}
                </ErrorText>
              )}
            </FormGroup>

            <FormGroup>
              <InputLabel htmlFor="agent-id">Agent ID</InputLabel>
              <TextInput
                id="agent-id"
                value={form.agentId}
                onChange={handleFieldChange('agentId')}
                placeholder="Enter agent ID"
                $hasError={!!errors.agentId}
                disabled={mutation.isPending}
                aria-describedby={errors.agentId ? 'agent-id-error' : undefined}
              />
              {errors.agentId && (
                <ErrorText id="agent-id-error" role="alert">
                  {errors.agentId}
                </ErrorText>
              )}
            </FormGroup>

            <FormGroup>
              <InputLabel htmlFor="alias-id">Alias ID</InputLabel>
              <TextInput
                id="alias-id"
                value={form.aliasId}
                onChange={handleFieldChange('aliasId')}
                placeholder="Enter alias ID"
                $hasError={!!errors.aliasId}
                disabled={mutation.isPending}
                aria-describedby={errors.aliasId ? 'alias-id-error' : undefined}
              />
              {errors.aliasId && (
                <ErrorText id="alias-id-error" role="alert">
                  {errors.aliasId}
                </ErrorText>
              )}
            </FormGroup>

            <FormGroup>
              <InputLabel htmlFor="site-link">Site link</InputLabel>
              <TextInput
                id="site-link"
                type="url"
                value={form.siteLink}
                onChange={handleFieldChange('siteLink')}
                placeholder="https://www.example.com"
                $hasError={!!errors.siteLink}
                disabled={mutation.isPending}
                aria-describedby={errors.siteLink ? 'site-link-error' : undefined}
              />
              {errors.siteLink && (
                <ErrorText id="site-link-error" role="alert">
                  {errors.siteLink}
                </ErrorText>
              )}
            </FormGroup>

            <FormGroup>
              <InputLabel htmlFor="chatbot-type">Chatbot type</InputLabel>
              <SelectInput
                id="chatbot-type"
                value={form.chatbotType}
                onChange={handleFieldChange('chatbotType')}
                $hasError={!!errors.chatbotType}
                disabled={mutation.isPending}
                aria-describedby={errors.chatbotType ? 'chatbot-type-error' : undefined}
              >
                <option value="" disabled>
                  Select chatbot type
                </option>
                {CHATBOT_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </SelectInput>
              {errors.chatbotType && (
                <ErrorText id="chatbot-type-error" role="alert">
                  {errors.chatbotType}
                </ErrorText>
              )}
            </FormGroup>

            <FormGroup>
              <InputLabel htmlFor="site-logo">Site logo</InputLabel>
              <FilePickerButton
                type="button"
                onClick={handlePickLogo}
                disabled={mutation.isPending || logoMutation.isPending}
                $hasError={!!errors.siteLogo}
              >
                {logoMutation.isPending ? (
                  <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                ) : (
                  <ImagePlus size={16} />
                )}
                {logoMutation.isPending
                  ? 'Uploading…'
                  : logoPreview
                    ? 'Change image'
                    : 'Choose an image'}
              </FilePickerButton>

              {logoPreview && (
                <FilePreviewRow>
                  <ImagePreview src={logoPreview} alt="Site logo preview" />
                  <FileNameText>{logoFileName || 'Current logo'}</FileNameText>
                  {!isUpdate && (
                    <RemoveFileButton
                      type="button"
                      onClick={handleRemoveLogo}
                      disabled={mutation.isPending}
                      aria-label="Remove logo"
                    >
                      <X size={14} />
                    </RemoveFileButton>
                  )}
                </FilePreviewRow>
              )}

              <HiddenFileInput
                id="site-logo"
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleLogoChange}
                aria-hidden="true"
              />
              {errors.siteLogo && (
                <ErrorText id="site-logo-error" role="alert">
                  {errors.siteLogo}
                </ErrorText>
              )}
            </FormGroup>

            {errors.form && <ErrorText role="alert">{errors.form}</ErrorText>}
          </ModalBody>

          <ModalFooter>
            <ModalCancelButton type="button" onClick={handleClose}>
              Cancel
            </ModalCancelButton>
            <ModalSubmitButton type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? (
                <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
              ) : null}
              {mutation.isPending
                ? isUpdate
                  ? 'Updating…'
                  : 'Creating…'
                : isUpdate
                  ? 'Update agent'
                  : 'Create agent'}
            </ModalSubmitButton>
          </ModalFooter>
        </form>
      </ModalContent>
    </ModalOverlay>,
    document.body
  );
};

export default CreateAndUpdateCouncilAgent;
